import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_RECIPES_GROUPS = 'getRecipesGroups'
export const GET_RECIPE_GROUP = 'getRecipeGroup'
export const MANAGE_RECIPE_GROUP = 'manageRecipeGroup'
export const DELETE_RECIPE_GROUP = 'deleteRecipeGroup'
export const GET_RECIPE_GROUP_RECIPES = 'getRecipeGroupRecipes'
export const ADD_NEW_RECIPE = 'addNewRecipe'
export const SORT_RECIPE_GROUP_RECIPES = 'sortRecipeGroupRecipes'
export const DELETE_RECIPE_GROUP_RECIPE = 'deleteRecipeGroupRecipe'

// mutation types
export const SAVE_RECIPES_GROUPS = 'saveRecipesGroups'
export const SAVE_RECIPE_GROUP = 'saveRecipeGroup'
export const CLEAR_RECIPES_GROUPS = 'clearRecipesGroups'
export const SELECT_RECIPE_GROUP = 'selectRecipeGroup'
export const UNSELECT_RECIPE_GROUP = 'unselectRecipeGroup'
export const SAVE_MANAGE_RECIPE_GROUP_ERRORS = 'saveManageRecipeGroupErrors'
export const CLEAR_MANAGE_RECIPE_GROUP_ERRORS = 'clearManageRecipeGroupErrors'
export const SAVE_RECIPE_GROUP_RECIPES = 'saveRecipeGroupRecipes'
export const CLEAR_RECIPE_GROUP_RECIPES = 'clearRecipeGroupRecipes'
export const SAVE_PENDING_RECIPE_GROUP_RECIPE_ID = 'savePendingRecipeGroupRecipeId'
export const CLEAR_PENDING_RECIPE_GROUP_RECIPE_ID = 'clearPendingRecipeGroupRecipeId'
export const SAVE_RECIPE_GROUP_RECIPE = 'saveRecipeGroupRecipe'
export const CLEAR_RECIPE_GROUP_RECIPE = 'clearRecipeGroupRecipe'

const SET_RESPONSE_STATE = 'setResponseState'
const TOAST_RECIPE_GROUPS = 'showToast/SettingsRecipesGroups'

export const DEFAULT_SELECTED_RECIPE_GROUP = {
  name: '',
  recipes: 0,
  servers: 0,
  recipes_list: [],
}

export const DEFAULT_SELECTED_RECIPE_GROUP_RECIPE = {
  name: '',
  description: '',
  version: '',
  tags: [],
}

// returns group based on _id
function findRecipeGroup(state, id) {
  return state.recipesGroupsList.filter(item => item._id === id)[0]
}

// returns recipe from .recipes_list[] array included in recipe group
// requires both recipe group id and id of recipe inside that group
function findRecipeGroupRecipe(state, { recipe_group_id, recipe_id }) {
  const targetRecipeGroup = state.recipesGroupsList.filter(item => item._id === recipe_group_id)[0]
  if (
    targetRecipeGroup.recipes_list
    && Array.isArray(targetRecipeGroup.recipes_list)
    && targetRecipeGroup.recipes_list.length == 0) {
    return targetRecipeGroup.recipes_list.filter(recipe => recipe._id === recipe_id)[0]
  }
}

function convertForFrontend(state, group) {
  // add _showDetails param recipe group because we want possibility to expand rows
  // also keep .recipes_list if user already opened them
  // see => https://bootstrap-vue.org/docs/components/table#row-details-support
  const target = findRecipeGroup(state, group._id)
  if (!target) {
    group._showDetails = false
    group.recipes_list = []
  } else {
    group._showDetails = target._showDetails
    if (
      target.recipes_list &&
      target.recipes_list.length > 0) {
        group.recipes_list = target.recipes_list
    }
  }

  return group
}

const state = {
  recipesGroupsList: [],
  selectedRecipeGroup: { ...DEFAULT_SELECTED_RECIPE_GROUP },
  selectedRecipeGroupId: null,
  manageRecipeGroupErrors: [],
  pendingRecipeGroups: [],
  selectedRecipeGroupRecipe: { ...DEFAULT_SELECTED_RECIPE_GROUP_RECIPE },
  selectedRecipeGroupRecipeId: null,

  responses: {
    recipesGroupsList: false,
    getRecipeGroup: false,
    manageRecipeGroup: false,
    deleteRecipeGroup: false,
    getRecipeGroupRecipes: false,
    addNewRecipe: false,
    sortRecipeGroupRecipes: false,
    deleteRecipeGroupRecipe: false,
  },
}

const getters = {
  recipesGroupsList(state) {
    return state.recipesGroupsList
  },
  recipesGroupsEmpty(state) {
    return state.recipesGroupsList.length == 0
  },
  totalRecipesGroups(state) {
    return state.recipesGroupsList.length
  },
  selectedRecipeGroup(state) {
    return state.selectedRecipeGroup
  },
  selectedRecipeGroupId(state) {
    return state.selectedRecipeGroupId
  },
  selectedRecipeGroupsExist(state) {
    return state.recipesGroupsList.some(group => group._id == state.selectedRecipeGroupId)
  },
  recipeGroupPending: state => id => {
    return state.pendingRecipeGroups.some(i => i == id)
  },
  recipeGroupRecipes: state => id => {
    const target = findRecipeGroup(state, id)
    return target ? target.recipes_list : []
  },
  recipeGroupRecipesEmpty: state => id => {
    const target = findRecipeGroup(state, id)
    if (target) {
      return target.recipes_list
      && Array.isArray(target.recipes_list)
      && target.recipes_list.length == 0
    }
  },
  recipeGroupRecipeHasDescription: state => (recipe_group_id, recipe_id) => {
    const targetRecipe = findRecipeGroupRecipe(state, { recipe_group_id, recipe_id })
    return targetRecipe.description && targetRecipe.description.length > 0
  },
  recipesGroupsModalTitle(state) {
    return state.selectedRecipeGroupId ? 'Edit Recipe Group' : 'Add New Recipe Group'
  },
  manageRecipeGroupErrors(state) {
    return state.manageRecipeGroupErrors
  },
  manageRecipeGroupError: state => field => {
    return state.manageRecipeGroupErrors.filter(err => err.field == field)
  },
  recipeGroupsResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_RECIPES_GROUPS](context, { action }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'recipesGroupsList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(process.env.VUE_APP_RECIPES_GROUPS)
          .then(({ data }) => {
            context.commit(SAVE_RECIPES_GROUPS, { action, groups: data })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'recipesGroupsList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_RECIPE_GROUP](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getRecipeGroup', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RECIPES_GROUP}/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_RECIPE_GROUP, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getRecipeGroup', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_RECIPE_GROUP](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageRecipeGroup', status: true })
        eventBus.$emit('startProgress')

        let method
        let url

        if (context.getters.selectedRecipeGroupsExist) {
          method = 'put'
          url = `${process.env.VUE_APP_RECIPES_GROUP}/${state.selectedRecipeGroupId}`
        } else {
          method = 'post'
          url = process.env.VUE_APP_RECIPES_GROUP
        }

        ApiService[method](url, state.selectedRecipeGroup)
          .then(({ data }) => {
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_RECIPE_GROUP_ERRORS, error.errors)
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageRecipeGroup', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_RECIPE_GROUP](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipeGroup', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_RECIPES_GROUP}/${id}`)
          .then(({ data }) => {
            context.dispatch(GET_RECIPES_GROUPS, { action: 'refresh' })
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_RECIPE_GROUPS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipeGroup', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_RECIPE_GROUP_RECIPES](context, { id, isShowing }) {
    if (!isShowing) {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getRecipeGroupRecipes', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        context.commit(SAVE_PENDING_RECIPE_GROUP_RECIPE_ID, id)
        ApiService.get(`${process.env.VUE_APP_RECIPES_GROUPS}/${id}/recipes`)
          .then(({ data }) => {
            context.commit(SAVE_RECIPE_GROUP_RECIPES, {
              recipes: data,
              id: id
            })
          })
          .catch(err => {
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: err.message,
              variant: 'danger'
            })
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getRecipeGroupRecipes', status: false })
            eventBus.$emit('endProgress')
            context.commit(CLEAR_PENDING_RECIPE_GROUP_RECIPE_ID, id)
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    }
  },
  [ADD_NEW_RECIPE](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'addNewRecipe', status: true })
        eventBus.$emit('startProgress')
        ApiService.post(
          `${process.env.VUE_APP_RECIPES_GROUPS}/${context.state.selectedRecipeGroupId}/recipe`,
          {
            recipe: context.rootGetters['selectedRecipeId']
          })
          .then(({ data }) => {
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'addNewRecipe', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SORT_RECIPE_GROUP_RECIPES](context, { id, order }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'sortRecipeGroupRecipes', status: true })
        eventBus.$emit('startProgress')
        ApiService.patch(`${process.env.VUE_APP_RECIPES_GROUPS}/${id}/recipes/sort`, order)
          .then(({ data }) => {
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: 'Recipe order changed',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_RECIPE_GROUPS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'sortRecipeGroupRecipes', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_RECIPE_GROUP_RECIPE](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipeGroupRecipe', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_RECIPES_GROUPS}/${state.selectedRecipeGroupId}/recipe/${state.selectedRecipeGroupRecipeId}`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_RECIPE_GROUPS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_RECIPE_GROUPS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipeGroupRecipe', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_RECIPES_GROUPS](state, { action, groups }) {
    if (groups.length > 0) {
      let newItems = []
      for (let i = 0; i < groups.length; i++) {
        const convertedGroup = convertForFrontend(state, groups[i])
        if (action === 'start') {
          newItems.push(convertedGroup)
          state.recipesGroupsList = newItems
        }
        else if (action === 'refresh') {
          const targetGroup = findRecipeGroup(state, groups[i]._id)
          if (targetGroup) {
            Object.keys(convertedGroup).forEach(key => {
              Vue.set(targetGroup, key, convertedGroup[key])
            })
          } else {
            state.recipesGroupsList.push(convertedGroup)
          }
        }
      }
    }
  },
  [SAVE_RECIPE_GROUP](state, group) {
    const convertedGroup = convertForFrontend(state, group)
    const targetGroup = findRecipeGroup(state, group._id)
    if (targetGroup) {
      Object.keys(convertedGroup).forEach(key => {
        Vue.set(targetGroup, key, convertedGroup[key])
      })
    }
  },
  [CLEAR_RECIPES_GROUPS](state) {
    state.recipesGroupsList = []
  },
  [SELECT_RECIPE_GROUP](state, group) {
    Object.keys(group).forEach(key => {
      state.selectedRecipeGroup[key] = group[key]
    })
    state.selectedRecipeGroupId = group._id
  },
  [UNSELECT_RECIPE_GROUP](state) {
    state.selectedRecipeGroup = { ...DEFAULT_SELECTED_RECIPE_GROUP }
    state.selectedRecipeGroupId = null
  },
  [SAVE_MANAGE_RECIPE_GROUP_ERRORS](state, errors) {
    state.manageRecipeGroupErrors = errors
  },
  [CLEAR_MANAGE_RECIPE_GROUP_ERRORS](state) {
    state.manageRecipeGroupErrors = []
  },
  [SAVE_PENDING_RECIPE_GROUP_RECIPE_ID](state, id) {
    if (!state.pendingRecipeGroups.some(i => i == id)) {
      state.pendingRecipeGroups.push(id)
    }
  },
  [CLEAR_PENDING_RECIPE_GROUP_RECIPE_ID](state, id) {
    const index = state.pendingRecipeGroups.indexOf(id)
    if (index !== -1) {
      state.pendingRecipeGroups.splice(index, 1)
    }
  },
  [SAVE_RECIPE_GROUP_RECIPES](state, { recipes, id }) {
    const target = findRecipeGroup(state, id)

    Vue.set(target, 'recipes_list', recipes)
    Vue.set(state.selectedRecipeGroup, 'recipes_list', recipes)

    // modify .tags string -> array[] of string
    if (target.recipes_list.length > 0) {
      target.recipes_list.forEach(recipe => {
        if (recipe.tags && recipe.tags.length > 0) {
          recipe.tags = recipe.tags.split(' ')
        } else {
          recipe.tags = []
        }
      })
    }
  },
  [CLEAR_RECIPE_GROUP_RECIPES](state, id) {
    const target = findRecipeGroup(state, id)
    Vue.set(target, 'recipes_list', [])
    Vue.set(state.selectedRecipeGroup, 'recipes_list', [])
  },
  [SAVE_RECIPE_GROUP_RECIPE](state, version) {
    Object.assign(state.selectedRecipeGroupRecipe, version)
    state.selectedRecipeGroupRecipeId = version._id
  },
  [CLEAR_RECIPE_GROUP_RECIPE](state) {
    state.selectedRecipeGroupRecipe = {}
    Object.assign(state.selectedRecipeGroupRecipe, DEFAULT_SELECTED_RECIPE_GROUP_RECIPE)
    state.selectedRecipeGroupRecipeId = null
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
